import { type ReactNode, useState } from 'react';
import { Copy, KeyRound } from 'lucide-react';
import { useToast } from '@/components/ui/toast';
import { generatePassword } from '../utils/password-generator';

const BTN =
  'flex items-center gap-xs rounded-md border border-white/[0.08] bg-white/[0.04] px-sm py-1.5 text-xs text-text-secondary transition-colors hover:bg-white/[0.08] hover:text-text-primary';

type Props = {
  onGenerate: (pw: string) => void;
  current: string;
};

async function copyToClipboard(value: string): Promise<boolean> {
  try {
    await globalThis.navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function useGenAndCopy(onGenerate: (pw: string) => void, current: string) {
  const { toast } = useToast();
  const [lastGenerated, setLastGenerated] = useState('');
  const handleGenerate = (): void => {
    const pw = generatePassword();
    setLastGenerated(pw);
    onGenerate(pw);
  };
  const handleCopy = async (): Promise<void> => {
    const value = lastGenerated || current;
    if (!value) return;
    const ok = await copyToClipboard(value);
    toast(
      ok ? 'Contraseña copiada' : 'No se pudo copiar',
      ok ? 'success' : 'error'
    );
  };
  return { lastGenerated, handleGenerate, handleCopy };
}

export function PasswordGeneratorRow({
  onGenerate,
  current,
}: Props): ReactNode {
  const g = useGenAndCopy(onGenerate, current);
  return (
    <div className="flex flex-wrap gap-sm">
      <button type="button" onClick={g.handleGenerate} className={BTN}>
        <KeyRound size={12} />
        Generar contraseña
      </button>
      <button
        type="button"
        onClick={() => void g.handleCopy()}
        disabled={!g.lastGenerated && !current}
        className={`${BTN} disabled:opacity-40`}
      >
        <Copy size={12} />
        Copiar
      </button>
    </div>
  );
}
